import React, { useState } from "react";
import { Modal, Button, Card, Tooltip, notification } from "antd";
import {
  CheckOutlined,
  CrownOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { createCheckoutSession } from "../services/paymentsAPI";
import { useMutation } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth";

const FeatureItem = ({ text, tooltip }) => (
  <Tooltip title={tooltip}>
    <p className="flex items-center space-x-1">
      <CheckOutlined className="text-green-500" />
      <span>{text}</span>
      {tooltip && <InfoCircleOutlined className="text-gray-400 text-sm" />}
    </p>
  </Tooltip>
);

const PricingCard = ({
  title,
  price,
  features,
  buttonText,
  buttonIcon,
  isCurrentPlan,
  backgroundColor,
  onClick,
  loading,
}) => (
  <Card
    title={<span className="text-lg font-bold">{title}</span>}
    bordered={true}
    className="text-center shadow-lg transition-all duration-300 hover:shadow-xl h-full flex flex-col"
    headStyle={{ backgroundColor, fontWeight: "bold" }}
    bodyStyle={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
  >
    <div className="flex-grow">
      <p className="text-3xl font-bold mb-4">{price}</p>
      {features.map((feature, index) => (
        <FeatureItem
          key={index}
          text={feature.text}
          tooltip={feature.tooltip}
        />
      ))}
    </div>
    <Button
      type={isCurrentPlan ? "default" : "primary"}
      icon={loading ? <LoadingOutlined /> : buttonIcon}
      className="w-full mt-4"
      onClick={onClick}
      disabled={isCurrentPlan || loading}
    >
      {loading ? "Processing..." : buttonText}
    </Button>
  </Card>
);

const UpgradeWarningModal = ({ visible, onClose, text, mode = "upgrade" }) => {
  const [selectedTier, setSelectedTier] = useState(null);
  const { selectedProject } = useAuth();

  const createCheckoutMutation = useMutation({
    mutationFn: createCheckoutSession,
    onSuccess: (response) => {
      window.location.href = response.checkout_url;
    },
    onError: (error) => {
      notification.error({
        message:
          "There was an error processing your request. Please try again later.",
        description: error.message,
      });
    },
  });

  const handleSubscription = async (plan) => {
    setSelectedTier(plan);
    if (mode === "upgrade") {
      createCheckoutMutation.mutate({
        plan: plan,
        projectId: selectedProject.project_id,
      });
    } else {
      createCheckoutMutation.mutate({ plan: plan });
    }
  };

  const tiers = [
    {
      title: "Free Tier",
      price: "$0/month",
      features: [
        { text: "1 Project" },
        { text: "Unlimited Users" },
        { text: "Basic Features" },
      ],
      buttonText: "Current Plan",
      backgroundColor: "#e6f7ff",
      isCurrentPlan: true,
    },
    {
      title: "Professional Tier",
      price: "$200/month",
      features: [
        { text: "Unlimited Users" },
        { text: "10 GB Storage" },
        {
          text: "AI Features",
          tooltip: "Includes advanced AI-powered analytics and automation",
        },
        {
          text: "Q&A Bots",
          tooltip: "Ask questions to your construction data",
        },
      ],
      buttonText: "Upgrade to Professional",
      buttonIcon: <CheckOutlined />,
      backgroundColor: "#f0f0f0",
      onClick: () => handleSubscription("professional"),
    },
    {
      title: "Enterprise Tier",
      price: "$300/month",
      features: [
        { text: "Unlimited Users" },
        { text: "Unlimited Storage" },
        {
          text: "Advanced AI Features",
          tooltip:
            "Includes all Professional features plus custom AI model training",
        },
        { text: "Priority Support", tooltip: "Dedicated support team" },
      ],
      buttonText: "Upgrade to Enterprise",
      buttonIcon: <CrownOutlined />,
      backgroundColor: "#fef0f0",
      onClick: () => handleSubscription("enterprise"),
    },
  ];

  return (
    <Modal
      title={<h2 className="text-2xl font-bold text-center">{text}</h2>}
      open={visible}
      onCancel={onClose}
      footer={null}
      centered
      width={1000}
      className="upgrade-modal"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
        {tiers.map((tier, index) => (
          <PricingCard
            key={index}
            {...tier}
            loading={
              createCheckoutMutation.isPending &&
              selectedTier === tier.title.split(" ")[0].toLowerCase()
            }
          />
        ))}
      </div>
    </Modal>
  );
};

export default UpgradeWarningModal;
